// src/components/Seguridad/Components/Tabs/UsuarioRolTab.jsx
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../Utils/Endpoints';
import { Models } from '../../Utils/ModelsSeguridad';
import AlertDialog from '../../../Lms/Components/Shared/AlertDialog';
import { isSessionSuperAdmin } from '../../../../utils/SessionUser';

const TITLETAB = 'Asignación de Roles';
const endpointUsuarioRol = ENDPOINTS.usuarioRol;
const endpointUsuarioRolById = ENDPOINTS.usuarioRolById;
const endpointRoles = ENDPOINTS.roles;

const normalizeUsuarioRol = Models.usuarioRol;
const normalizeRol = Models.roles;

export default function UsuarioRolTab({ fkValue, onClose }) {
    const [loading, setLoading] = useState(false);
    const [allRoles, setAllRoles] = useState([]);
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

    // State for initial data (to calculate changes)
    const [initialAssignedRoles, setInitialAssignedRoles] = useState([]);

    // State for current local changes
    const [currentAssignedRoles, setCurrentAssignedRoles] = useState([]);

    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [saving, setSaving] = useState(false);

    // State for user information
    const [userInfo, setUserInfo] = useState(null);
    const [personaInfo, setPersonaInfo] = useState(null);
    const [empleadoInfo, setEmpleadoInfo] = useState(null);
    const [puestoInfo, setPuestoInfo] = useState(null);
    const [unidadOrgInfo, setUnidadOrgInfo] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            console.log('[UsuarioRolTab] Loading roles for user:', fkValue);
            const [resRoles, resUserRoles] = await Promise.all([
                axios.get(endpointRoles),
                axios.get(ENDPOINTS.usuarioRolByUsuarioId(fkValue)),
            ]);

            let roles = (resRoles?.data || []).map(normalizeRol);
            
            // Only SuperAdmins can assign SuperAdmin roles
            if (!isSessionSuperAdmin()) {
                roles = roles.filter(r => !r.nombre.toLowerCase().includes('super'));
            }

            const myUserRoles = (resUserRoles?.data || []).map(normalizeUsuarioRol);

            console.log('[UsuarioRolTab] Roles loaded:', roles.length);
            console.log('[UsuarioRolTab] User roles assigned:', myUserRoles.length, myUserRoles);

            setAllRoles(roles);
            setInitialAssignedRoles(myUserRoles);
            setCurrentAssignedRoles(myUserRoles);
        } catch (e) {
            console.error('[UsuarioRolTab] Error loading data:', e);
            setAlertDialog({ open: true, title: 'Error', message: `Error al cargar ${TITLETAB}`, variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadUserInfo = async () => {
        try {
            // 1. Cargar información del usuario - Obtener todos y filtrar
            const resUsers = await axios.get(ENDPOINTS.usuarios);
            const usuarios = (resUsers?.data || []).map(Models.usuarios);
            const user = usuarios.find(u => Number(u.idUsuario) === Number(fkValue));

            if (!user) {
                console.warn('Usuario no encontrado');
                return;
            }

            setUserInfo(user);

            if (!user.idPersona) return;

            // 2. Cargar información de la persona - Obtener todas y filtrar (General copiado en Seguridad)
            const resPersonas = await axios.get(ENDPOINTS.personas);
            const personas = (resPersonas?.data || []).map(Models.personas);
            const persona = personas.find(p => Number(p.idPersona) === Number(user.idPersona));

            if (!persona) {
                console.warn('Persona no encontrada');
                return;
            }

            setPersonaInfo(persona);

            // 3. Cargar información del empleado (si existe)
            const resEmpleados = await axios.get(ENDPOINTS.empleados);
            const empleados = (resEmpleados?.data || []).map(Models.empleado);
            const empleado = empleados.find(e => Number(e.idPersona) === Number(user.idPersona));

            if (empleado) {
                setEmpleadoInfo(empleado);

                // 4. Cargar puesto del empleado (si existe)
                const resEmpleadosPuesto = await axios.get(ENDPOINTS.empleadosPuesto);
                const empleadosPuesto = (resEmpleadosPuesto?.data || []).map(Models.empleadosPuesto);
                const empleadoPuesto = empleadosPuesto.find(ep => Number(ep.idEmpleado) === Number(empleado.idEmpleado));

                if (empleadoPuesto) {
                    const resPuestos = await axios.get(ENDPOINTS.puestos);
                    const puestos = (resPuestos?.data || []).map(Models.puesto);
                    const puesto = puestos.find(p => Number(p.idPuesto) === Number(empleadoPuesto.idPuesto));
                    setPuestoInfo(puesto);
                }

                // 5. Cargar unidad organizacional del empleado (si existe)
                const resUnidadesOrgEmpleado = await axios.get(ENDPOINTS.unidadesOrgEmpleado);
                const unidadesOrgEmpleado = (resUnidadesOrgEmpleado?.data || []).map(Models.unidadOrgEmpleado);
                const unidadOrgEmpleado = unidadesOrgEmpleado.find(ue => Number(ue.idEmpleado) === Number(empleado.idEmpleado));

                if (unidadOrgEmpleado) {
                    const resUnidadesOrg = await axios.get(ENDPOINTS.unidadesOrg);
                    const unidadesOrg = (resUnidadesOrg?.data || []).map(Models.unidadesOrg);
                    const unidadOrg = unidadesOrg.find(u => Number(u.idUnidadOrganizacional) === Number(unidadOrgEmpleado.idUnidadOrganizacional));
                    setUnidadOrgInfo(unidadOrg);
                }
            }
        } catch (e) {
            console.error('Error al cargar información del usuario:', e);
        }
    };

    useEffect(() => {
        if (fkValue) {
            load();
            loadUserInfo();
        }
    }, [fkValue]);

    // Calculate available roles (All Roles - Current Assigned Roles)
    const availableRoles = useMemo(() => {
        const assignedIds = new Set(currentAssignedRoles.map(r => r.idRol));
        return allRoles.filter(r => !assignedIds.has(r.idRol));
    }, [allRoles, currentAssignedRoles]);

    // Enriched assigned roles (add role name)
    const enrichedAssignedRoles = useMemo(() => {
        return currentAssignedRoles.map(ur => {
            const roleDef = allRoles.find(r => r.idRol === ur.idRol);
            return {
                ...ur,
                nombreRol: roleDef?.nombre || `Rol #${ur.idRol}`,
                descripcion: roleDef?.descripcion
            };
        });
    }, [currentAssignedRoles, allRoles]);

    // Check if there are unsaved changes
    const hasChanges = useMemo(() => {
        if (initialAssignedRoles.length !== currentAssignedRoles.length) return true;

        const initialIds = new Set(initialAssignedRoles.map(r => r.idRol));
        const currentIds = new Set(currentAssignedRoles.map(r => r.idRol));

        // Check if sets are equal
        for (let id of currentIds) {
            if (!initialIds.has(id)) return true;
        }
        return false;
    }, [initialAssignedRoles, currentAssignedRoles]);

    // --- Drag and Drop Handlers ---

    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, targetColumn) => {
        e.preventDefault();
        if (dragOverColumn !== targetColumn) {
            setDragOverColumn(targetColumn);
        }
    };

    const handleDrop = (e, targetColumn) => {
        e.preventDefault();
        setDragOverColumn(null);

        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);

        if (source === targetColumn) return;

        if (targetColumn === 'assigned' && source === 'available') {
            addRoleLocal(item);
        } else if (targetColumn === 'available' && source === 'assigned') {
            removeRoleLocal(item);
        }
    };

    // --- Local State Modifiers ---

    const addRoleLocal = (roleItem) => {
        const exists = currentAssignedRoles.some(r => r.idRol === roleItem.idRol);
        if (exists) return;

        const newAssignment = {
            idUsuarioRol: 0, // Temporary ID
            idUsuario: Number(fkValue),
            idRol: roleItem.idRol,
            fechaRegistro: new Date().toISOString(),
            activo: true
        };

        setCurrentAssignedRoles(prev => [...prev, newAssignment]);
    };

    const removeRoleLocal = (userRoleItem) => {
        setCurrentAssignedRoles(prev => prev.filter(r => r.idRol !== userRoleItem.idRol));
    };

    // --- Save / Cancel ---

    const handleCancel = () => {
        onClose?.();
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // 1. Identify roles to ADD (in current but not in initial)
            const initialIds = new Set(initialAssignedRoles.map(r => r.idRol));
            const toAdd = currentAssignedRoles.filter(r => !initialIds.has(r.idRol));

            // 2. Identify roles to REMOVE (in initial but not in current)
            const currentIds = new Set(currentAssignedRoles.map(r => r.idRol));
            const toRemove = initialAssignedRoles.filter(r => !currentIds.has(r.idRol));

            // 3. Execute requests
            const promises = [];

            // Additions
            for (const item of toAdd) {
                const payload = {
                    IdUsuarioRol: 0,
                    IdUsuario: Number(fkValue),
                    IdRol: item.idRol,
                    FechaRegistro: new Date().toISOString(),
                    Activo: true
                };
                promises.push(axios.post(endpointUsuarioRol, payload));
            }

            // Removals
            for (const item of toRemove) {
                // We need the original ID to delete
                if (item.idUsuarioRol > 0) {
                    promises.push(axios.delete(endpointUsuarioRolById(item.idUsuarioRol)));
                }
            }

            await Promise.all(promises);

            // Reload to sync with DB
            await load();

        } catch (e) {
            console.error(e);
            setAlertDialog({ open: true, title: 'Error', message: 'Error al guardar los cambios.', variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mini-crud">
            <div className="mini-topbar">
                <div>
                    <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>

                    {/* Información del Usuario */}
                    {userInfo && personaInfo && (
                        <div style={{
                            marginTop: '0.8rem',
                            padding: '0.8rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            fontSize: '0.9rem'
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                <div>
                                    <strong>Usuario:</strong> {userInfo.nombreUsuario}
                                </div>
                                <div>
                                    <strong>Nombre:</strong> {`${personaInfo.nombre} ${personaInfo.apellidoPaterno} ${personaInfo.apellidoMaterno || ''}`.trim()}
                                </div>
                                {puestoInfo && (
                                    <div>
                                        <strong>Puesto:</strong> {puestoInfo.nombre || 'N/A'}
                                    </div>
                                )}
                                {unidadOrgInfo && (
                                    <div>
                                        <strong>Unidad Organizacional:</strong> {unidadOrgInfo.nombre || 'N/A'}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        Arrastra o usa los botones (+ / x) para modificar. Guarda al finalizar.
                    </div>
                </div>
            </div>

            <div className="dnd-container">
                {/* Left: Available Roles */}
                <div
                    className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, 'available')}
                    onDrop={(e) => handleDrop(e, 'available')}
                >
                    <div className="dnd-header">Roles Disponibles (Origen)</div>
                    {loading && availableRoles.length === 0 ? (
                        <div className="dnd-empty">Cargando...</div>
                    ) : availableRoles.length === 0 ? (
                        <div className="dnd-empty">No hay roles disponibles</div>
                    ) : (
                        availableRoles.map(role => (
                            <div
                                key={role.idRol}
                                className="dnd-item"
                                draggable={!saving}
                                onDragStart={(e) => handleDragStart(e, role, 'available')}
                            >
                                <span>{role.nombre}</span>
                                <button
                                    type="button"
                                    onClick={() => addRoleLocal(role)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.2em',
                                        color: '#28a745',
                                        cursor: 'pointer',
                                        padding: '0 5px'
                                    }}
                                    title="Agregar rol"
                                >
                                    ＋
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {/* Right: Assigned Roles */}
                <div
                    className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, 'assigned')}
                    onDrop={(e) => handleDrop(e, 'assigned')}
                >
                    <div className="dnd-header">Roles Asignados (Destino)</div>
                    {loading && enrichedAssignedRoles.length === 0 ? (
                        <div className="dnd-empty">Cargando...</div>
                    ) : enrichedAssignedRoles.length === 0 ? (
                        <div className="dnd-empty">Arrastra roles aquí para asignarlos</div>
                    ) : (
                        enrichedAssignedRoles.map(ur => (
                            <div
                                key={ur.idRol} // Use idRol as key because idUsuarioRol might be 0 for new items
                                className="dnd-item"
                                draggable={!saving}
                                onDragStart={(e) => handleDragStart(e, ur, 'assigned')}
                            >
                                <span>{ur.nombreRol}</span>
                                <button
                                    type="button"
                                    onClick={() => removeRoleLocal(ur)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.2em',
                                        color: '#dc3545',
                                        cursor: 'pointer',
                                        padding: '0 5px'
                                    }}
                                    title="Remover rol"
                                >
                                    ×
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
                <button
                    className="roles-btn"
                    onClick={handleCancel}
                    type="button"
                >
                    Cancelar
                </button>
                <button
                    className="btn-action"
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    style={{
                        opacity: (!hasChanges || saving) ? 0.5 : 1,
                        cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer'
                    }}
                    type="button"
                >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>

            {alertDialog.open && (
                <AlertDialog
                    open={alertDialog.open}
                    onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
                    title={alertDialog.title}
                    message={alertDialog.message || ""}
                    variant={alertDialog.variant || "info"}
                />
            )}
        </div>
    );
}
